import {
  ActivityWebCalendarSettings,
  PersonalAppointmentsWebCalendarSettings,
} from "@/orval/gen"
import {
  CancelButton,
  CreateButton,
  DeleteButtonWithConfirmModal,
  SaveButton,
} from "@components/Buttons"
import { FormButtonGroup } from "@components/form-button-group"
import { InfiniteUserMultiSelect } from "@components/InfiniteUserSelect"
import { Select, Stack, TextInput, Textarea } from "@mantine/core"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { ActivityCalendarFields } from "./ActivityCalendarForm"
import { PersonalCalendarFields } from "./PersonalCalendarFields"

export interface CalendarProfileFormValues {
  name: string
  description: string
  provider: "appointments" | "activities"
  personalSettings: PersonalAppointmentsWebCalendarSettings
  activitySettings: ActivityWebCalendarSettings
  visibleForUserIds: number[]
}

export const defaultCalendarProfileValues: CalendarProfileFormValues = {
  name: "",
  description: "",
  provider: "appointments",
  personalSettings: {
    includeCastAppointments: true,
    includePossibleCastAppointments: false,
    includeDutyRosterAppointments: true,
    includeAbsenceAppointments: true,
    includeTravelAppointments: false,
    daysOffsetInPast: 45,
    daysOffsetInFuture: 365,
  },
  activitySettings: {},
  visibleForUserIds: [],
}

interface Props {
  initialValues: CalendarProfileFormValues
  isShared: boolean
  isEditing?: boolean
  onDelete?: () => void
  isDeleting?: boolean
  onClose: () => void
  onSubmit: (values: CalendarProfileFormValues) => Promise<void>
}

export function CalendarProfileForm({
  initialValues,
  isShared,
  isEditing = false,
  onDelete,
  isDeleting = false,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useTranslation("app_server")
  const form = useForm<CalendarProfileFormValues>({ initialValues })

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        {!isEditing && (
          <Select
            required
            allowDeselect={false}
            label={t("Calendar_Subscriptions_ProfileType")}
            data={[
              {
                value: "appointments",
                label: t("Calendar_Subscriptions_PersonalAppointments"),
              },
              { value: "activities", label: t("Activities") },
            ]}
            {...form.getInputProps("provider")}
          />
        )}
        <TextInput
          required
          autoFocus
          maxLength={50}
          label={t("Name")}
          placeholder={t("Calendar_Subscriptions_CreateModalNamePlaceholder")}
          {...form.getInputProps("name")}
        />
        <Textarea
          maxLength={255}
          label={t("Description")}
          placeholder={t(
            "Calendar_Subscriptions_CreateModalDescriptionPlaceholder"
          )}
          {...form.getInputProps("description")}
        />
        {form.values.provider === "activities" ? (
          <ActivityCalendarFields form={form} />
        ) : (
          <PersonalCalendarFields form={form} />
        )}
        {isShared && (
          <InfiniteUserMultiSelect
            label={t("Calendar_Subscriptions_Visibility")}
            value={form.values.visibleForUserIds.map(String)}
            onSelectionChange={(value) =>
              form.setFieldValue("visibleForUserIds", value.map(Number))
            }
            filterOptions={{ userTypeFilter: "Groups" }}
          />
        )}
        <FormButtonGroup>
          <CancelButton onClick={onClose} />
          {isEditing && onDelete && (
            <DeleteButtonWithConfirmModal
              type="button"
              color="red"
              variant="light"
              loading={isDeleting}
              disabled={form.submitting}
              confirmModalProps={{ onConfirm: onDelete }}
            />
          )}
          {isEditing ? (
            <SaveButton loading={form.submitting} disabled={isDeleting} />
          ) : (
            <CreateButton loading={form.submitting} />
          )}
        </FormButtonGroup>
      </Stack>
    </form>
  )
}
